import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type { MultiSelectComboboxOption } from '@ooh/ui/components/multi-select-combobox';
import { toast } from '@ooh/ui/components/sonner';
import { adminUsers, CreateUserSchema, parseApiError, permissions } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { FormSkeleton } from '../../layouts/skeletons';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import { createPasswordFieldSchema } from './password-field-schema';
import { invalidateUserQueries } from './user-queries';
import { RolePickerField } from './edit-user-dialog-parts';

// CreateUserSchema (packages/contracts/src/schemas/user-admin.ts) stays the
// server-side/API source of truth and is still enforced there -- this
// client-only schema exists so the min-length and digit/symbol validation
// errors render in the active UI language. The password field rule itself is
// shared with set-password-dialog.tsx via createPasswordFieldSchema; only the
// confirm-password match refine is dialog-specific.
function createFormSchema(messages: {
  minLength: string;
  requiresNumberOrSymbol: string;
  confirmInvalid: string;
}) {
  return CreateUserSchema.extend({
    password: createPasswordFieldSchema(messages),
    confirmPassword: z.string(),
  }).refine((d) => d.password === d.confirmPassword, {
    message: messages.confirmInvalid,
    path: ['confirmPassword'],
  });
}

type CreateUserForm = z.infer<ReturnType<typeof createFormSchema>>;

const CREATE_USER_FORM_ID = 'create-user-form';

interface CreateUserDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

function CreateUserForm({ onClose, onSuccess }: CreateUserDialogProps) {
  const { t } = useTranslation('admin');
  const api = useApiClient();
  const queryClient = useQueryClient();

  const CreateUserFormSchema = createFormSchema({
    minLength: t('users.create.fields.minLength'),
    requiresNumberOrSymbol: t('users.create.fields.requiresNumberOrSymbol'),
    confirmInvalid: t('users.create.fields.confirmInvalid'),
  });

  const { data: roles } = useApiSuspenseQuery(permissions.getRoles, {});
  const roleOptions: MultiSelectComboboxOption[] = roles.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<CreateUserForm>({
    resolver: zodResolver(CreateUserFormSchema),
    mode: 'onChange',
  });

  const mutation = useMutation({
    mutationFn: (data: CreateUserForm) =>
      api.call(adminUsers.createUser, {
        email: data.email,
        name: data.name,
        roleIds: data.roleIds,
        password: data.password,
      }),
    onSuccess: () => {
      void invalidateUserQueries(queryClient);
      toast.success(t('users.actions.saved'));
      onSuccess();
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      if (parsed.statusCode === 409) {
        setError('email', { message: t('users.create.errors.emailTaken') });
      } else {
        toast.error(apiErrorToUserMessage(parsed, 'mutation'));
      }
    },
  });

  function onSubmit(data: CreateUserForm) {
    mutation.mutate(data);
  }

  return (
    <CreateUserDialogFrame
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('users.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form={CREATE_USER_FORM_ID}
            disabled={!isValid || mutation.isPending}
          >
            {mutation.isPending ? t('users.actions.saving') : t('users.actions.createUser')}
          </Button>
        </>
      }
    >
      <form id={CREATE_USER_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="create-email">{t('users.create.fields.email')}</Label>
          <Input
            id="create-email"
            type="email"
            placeholder={t('users.create.fields.emailPlaceholder')}
            {...register('email')}
          />
          {errors.email ? (
            <p className="text-[12px] text-destructive">{errors.email.message}</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="create-name">{t('users.create.fields.name')}</Label>
          <Input
            id="create-name"
            placeholder={t('users.create.fields.namePlaceholder')}
            {...register('name')}
          />
        </div>
        <Controller
          name="roleIds"
          control={control}
          render={({ field }) => (
            <RolePickerField
              value={field.value ?? []}
              onChange={field.onChange}
              options={roleOptions}
              label={t('users.create.fields.role')}
              placeholder={t('users.create.fields.rolePlaceholder')}
              searchPlaceholder={t('users.create.fields.roleSearchPlaceholder')}
              emptyText={t('users.create.fields.roleNoResults')}
              testId="create-role"
            />
          )}
        />
        <div className="space-y-1.5">
          <Label htmlFor="create-password">{t('users.create.fields.password')}</Label>
          <Input id="create-password" type="password" {...register('password')} />
          {errors.password ? (
            <p className="text-[12px] text-destructive">{errors.password.message}</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="create-confirm">{t('users.create.fields.confirmPassword')}</Label>
          <Input id="create-confirm" type="password" {...register('confirmPassword')} />
          {errors.confirmPassword ? (
            <p className="text-[12px] text-destructive">{errors.confirmPassword.message}</p>
          ) : null}
        </div>
      </form>
    </CreateUserDialogFrame>
  );
}

function CreateUserDialogFrame({
  onClose,
  footer,
  children,
}: {
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation('admin');

  return (
    <ModalDialog
      onClose={onClose}
      closeLabel={t('users.create.close')}
      header={<h2 className="text-[16px] font-semibold">{t('users.create.title')}</h2>}
      footer={footer}
    >
      {children}
    </ModalDialog>
  );
}

export function CreateUserDialog({ onClose, onSuccess }: CreateUserDialogProps) {
  return (
    <QueryBoundary
      fallback={<FormSkeleton />}
      renderFrame={(content) => (
        <CreateUserDialogFrame onClose={onClose}>{content}</CreateUserDialogFrame>
      )}
    >
      <CreateUserForm onClose={onClose} onSuccess={onSuccess} />
    </QueryBoundary>
  );
}
